// Implements: .claude/skills/rehaut-design/ui_kits/rehaut-pacientes/primitives.jsx (StatusDot)
// See: .claude/skills/rehaut-design/HANDOFF.md
'use client';

import type { CSSProperties } from 'react';

// Pacientes vocabulary first, gastos vocabulary appended. Both modules
// reuse this single primitive — no per-module variants, per the brief.
export type DotStatus =
  | 'activo'
  | 'pendiente'
  | 'inactivo'
  | 'pagado'
  | 'por-pagar'
  | 'vencido';

/**
 * 6px colored dot that conveys status without the visual weight of a
 * pill. `activo` pulses via the dotpulse keyframe (shipped in
 * globals.css) so a state change feels alive; everything else is
 * static.
 *
 * Per DESIGN_BRIEF: "Status conveyed by colored 6px dot before name.
 * No pills." This is the canonical implementation across v2 routes.
 */
export function StatusDot({
  status,
  style,
  label,
  pulse = true,
}: {
  status: DotStatus;
  style?: CSSProperties;
  /** Overrides the default `status` aria-label and adds a hover tooltip.
   *  Use when the dot means something other than its raw status token —
   *  e.g. notas maps complete→activo/incomplete→pendiente, so "activo"
   *  is the wrong thing to announce; pass "Nota completa" instead. */
  label?: string;
  /** The 'activo' breathing ring. Off for list rows: it is a box-shadow
   *  animation (style recalc + paint every frame) and a list of active
   *  patients had hundreds running for the life of the tab. Keep it for the
   *  one masthead dot. */
  pulse?: boolean;
}) {
  const bg =
    status === 'activo' || status === 'pagado'
      ? 'var(--dot-active)'
      : status === 'pendiente' || status === 'por-pagar'
        ? 'var(--dot-pending)'
        : status === 'vencido'
          ? 'var(--error)'
          : 'var(--dot-inactive)';
  return (
    <span
      role={label ? 'img' : 'presentation'}
      aria-label={label ?? status}
      title={label}
      style={{
        display: 'inline-block',
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: bg,
        flexShrink: 0,
        animation: pulse && status === 'activo' ? 'dotpulse 2.4s ease-out infinite' : undefined,
        ...style,
      }}
    />
  );
}
